import { useState, useEffect } from 'react';
import axios from 'axios';

const BASE_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
const API_URL = `${BASE_URL}/api`;

const normalizeImages = (images) => {
  const asUrl = (v) => {
    if (!v) return '';
    if (typeof v === 'string') return v.trim();
    if (typeof v === 'object') {
      if (typeof v.url === 'string') return v.url.trim();
      if (typeof v.src === 'string') return v.src.trim();
    }
    return '';
  };

  if (Array.isArray(images)) {
    return images.map(asUrl).filter(Boolean);
  }

  if (typeof images === 'string') {
    const s = images.trim();
    if (!s) return [];

    if (s.startsWith('[')) {
      try {
        const parsed = JSON.parse(s);
        if (Array.isArray(parsed)) return parsed.map(asUrl).filter(Boolean);
      } catch {}
    }

    return s
      .split(',')
      .map((x) => x.trim())
      .filter(Boolean);
  }

  if (typeof images === 'object' && images) {
    const u = asUrl(images);
    return u ? [u] : [];
  }

  return [];
};

const normalizeProduct = (p) => {
  if (!p || typeof p !== 'object') return p;
  return { ...p, images: normalizeImages(p.images) };
};

export const useProducts = (filters = {}) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(filters)]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.category_id) params.append('category_id', filters.category_id);
      if (filters.subcategory_id) params.append('subcategory_id', filters.subcategory_id);
      if (filters.search) params.append('search', filters.search);
      if (filters.status) params.append('status', filters.status);

      const response = await axios.get(`${API_URL}/products?${params.toString()}`);
      const data = Array.isArray(response.data) ? response.data : [];
      setProducts(data.map(normalizeProduct));
      setError(null);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };

  return { products, loading, error, refetch: fetchProducts };
};

export const useProduct = (slug) => {
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (slug) fetchProduct();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const fetchProduct = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/products/${slug}`);
      setProduct(normalizeProduct(response.data));
      setError(null);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };

  return { product, loading, error };
};

export const useCategories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/categories`);
      setCategories(Array.isArray(response.data) ? response.data : []);
      setError(null);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };

  return { categories, loading, error };
};

export const useSubcategories = (categoryId) => {
  const [subcategories, setSubcategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (categoryId) fetchSubcategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryId]);

  const fetchSubcategories = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/subcategories?category_id=${categoryId}`);
      setSubcategories(Array.isArray(response.data) ? response.data : []);
      setError(null);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };

  return { subcategories, loading, error };
};
